import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { useMissionSoftware, HSSI_HOME_URL } from "../pages/missionsoftware_utils.js";
import { SoftwareToolCards } from "./softwaretoolcards.js";
import hssiLogoImg from "../images/data-resources/hssi_logo.png";

const HSSI_NAME = "Heliophysics Software Search Interface";

function BrowseHssiLink() {
  return (
    <a className="missionSoftwareBrowseLink" href={HSSI_HOME_URL} target="_blank" rel="noopener noreferrer">
      Browse all heliophysics software on HSSI
      <FontAwesomeIcon icon={faUpRightFromSquare} className="softwareToolTitleIcon" aria-hidden="true" />
    </a>
  );
}

/**
 * Panel content for the Software tab on the mission page.
 *
 * Handles the loading, empty and error states itself and delegates the
 * list to renderTools so the layout can differ per branch.
 *
 * @param props.missionId hcut_id of the mission.
 * @param props.missionName Display name used in the messages.
 * @param props.renderTools Function (tools) => JSX. Defaults to SoftwareToolCards.
 * @return JSX
 */
export function MissionSoftwarePanel({ missionId, missionName, renderTools }) {
  const { status, tools } = useMissionSoftware(missionId);
  const render = renderTools || ((list) => <SoftwareToolCards tools={list} />);

  if (status === "loading") {
    return (
      <p className="missionSoftwareMessage" role="status">Loading software tools for {missionName}…</p>
    );
  }

  if (status === "error") {
    return (
      <div className="missionSoftwareMessage" role="status">
        <p className="missionSoftwareMessageText">Software tools for {missionName} could not be loaded right now.</p>
        <BrowseHssiLink />
      </div>
    );
  }

  if (tools.length === 0) {
    return (
      <div className="missionSoftwareMessage" role="status">
        <p className="missionSoftwareMessageText">
          No software tools are linked to {missionName} in the {HSSI_NAME} yet.
        </p>
        <BrowseHssiLink />
      </div>
    );
  }

  const noun = tools.length === 1 ? "software tool" : "software tools";
  return (
    <div>
      <div className="missionSoftwareIntro">
        <div className="missionSoftwareCount">{tools.length} {noun} linked to {missionName}</div>
        <div className="missionSoftwareSource">
          <span>
            From the <a href={HSSI_HOME_URL} target="_blank" rel="noopener noreferrer">{HSSI_NAME}</a>
          </span>
          <a
            className="missionSoftwareLogo"
            href={HSSI_HOME_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${HSSI_NAME} home`}
          >
            <img src={hssiLogoImg} alt="" />
          </a>
        </div>
      </div>
      {render(tools)}
    </div>
  );
}
